import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Trophy, Info } from 'lucide-react';
import { 
  LayoutDashboard, Zap, BookOpen, CheckCircle,
  Sun, Moon, Activity, Calendar, ArrowRight, ArrowLeft, Wine, Cigarette, Coffee, Smartphone, ShoppingCart, Gamepad2,
  Waves, Heart, Brain, Users, Target, Bell
} from 'lucide-react';
import { TypewriterMessage } from '@/components/onboarding/TypewriterMessage';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import WellnessRadar, { calculateDimensionScores } from './WellnessRadar';
import QuickCheckIn, { QuickCheckInData } from './QuickCheckIn';
import InsightsPanel from './InsightsPanel';
import ConnectedSelf from '@/components/ConnectedSelf';
import EnhancedSleepTracker from '@/components/EnhancedSleepTracker';
import EnhancedExerciseTracker from '@/components/EnhancedExerciseTracker';
import AlcoholTracker from '@/components/AlcoholTracker';
import HabitTracker from '@/components/HabitTracker';
import WaterTracker from '@/components/gamification/WaterTracker';
import SunlightTracker from '@/components/gamification/SunlightTracker';
import HabitPreferencesSelector from '@/components/HabitPreferencesSelector';
import SocialConnectionTracker from './SocialConnectionTracker';
import LifeExpansionCalendar from '@/components/LifeExpansionCalendar';
import PatternInsightsPanel from './PatternInsightsPanel';
import TherapeuticReflection from './TherapeuticReflection';
import PredictiveRiskMonitor from './PredictiveRiskMonitor';
import WeeklyReviewSummary from './WeeklyReviewSummary';
import DimensionDetailDialog from './DimensionDetailDialog';
import CheckInInstructions from './CheckInInstructions';
import ProgressCelebration from './ProgressCelebration';
import WellnessCorrelationEngine from './WellnessCorrelationEngine';
import PersonalisedChallenge from './PersonalizedChallenge';
import AITherappiSupport from './AITherappiSupport';
import WhoopImportButton from '@/components/whoop/WhoopImportButton';
import { RelationalPulsePrompt } from './RelationalPulsePrompt';
import PhysicalWellbeingCheckIn from './PhysicalWellbeingCheckIn';
import BalanceReflection from '@/components/balance/BalanceReflection';
import { useBalanceFromCurrentData } from '@/hooks/useBalanceProfile';
import { useCheckinStreak } from '@/hooks/useCheckinStreak';
import { ContextualNotificationPrompt } from '@/components/notifications/ContextualNotificationPrompt';
import { StreakDisplay } from './StreakDisplay';
import EveningSelfCareCheckIn from './EveningSelfCareCheckIn';
import ReminderSnooze from './ReminderSnooze';

type ViewMode = 'dashboard' | 'quick' | 'deep';
type CheckInType = 'morning' | 'evening';

interface CheckInData {
  [key: string]: any;
}

export default function DailyCheckInHub({ selectedDate }: { selectedDate?: string }) {
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [checkInType, setCheckInType] = useState<CheckInType>('morning');
  const [currentData, setCurrentData] = useState<CheckInData>({});
  const [recentData, setRecentData] = useState<any[]>([]);
  const targetDate = selectedDate || new Date().toISOString().split('T')[0];
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [hasWhoop, setHasWhoop] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [habitPreferences, setHabitPreferences] = useState<string[]>([]);
  const [showPreferencesSelector, setShowPreferencesSelector] = useState(false);
  const [userId, setUserId] = useState<string>('');
  const [showInsights, setShowInsights] = useState(true);
  const [selectedDimension, setSelectedDimension] = useState<any>(null);
  const [showDimensionDetail, setShowDimensionDetail] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [streak, setStreak] = useState(0);
  const [totalCheckIns, setTotalCheckIns] = useState(0);
  const { checkAndCelebrate } = useCheckinStreak();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Define habit steps based on preferences
  const habitSteps: { [key: string]: string } = {
    'exercise': 'Exercise & Movement',
    'sleep': 'Sleep Quality',
    'sunlight': 'Sunlight & Nature',
    'hydration': 'Hydration',
    'nutrition': 'Food & Nutrition',
    'alcohol': 'Alcohol Tracking',
    'smoking': 'Habit Tracking',
    'caffeine': 'Habit Tracking',
    'screen_time': 'Habit Tracking',
    'shopping': 'Habit Tracking',
    'gaming': 'Habit Tracking',
    'social': 'Social Connection'
  };

  // Build morning steps dynamically
  const buildMorningSteps = () => {
    const steps = ['Connected Self', 'Physical Wellbeing']; // Physical Wellbeing always after Connected Self
    const addedSteps = new Set<string>();
    
    habitPreferences.forEach(prefId => {
      const stepName = habitSteps[prefId];
      if (stepName && !addedSteps.has(stepName)) {
        steps.push(stepName);
        addedSteps.add(stepName);
      }
    });
    
    steps.push('Life Expansion');
    return steps;
  };

  const morningSteps = habitPreferences.length > 0 ? buildMorningSteps() : ['Connected Self', 'Physical Wellbeing', 'Life Expansion'];
  const eveningSteps = ['Daily Reflection', 'Evening Self-Care', 'Evening Ritual'];
  const steps = checkInType === 'morning' ? morningSteps : eveningSteps;

  useEffect(() => {
    loadCheckInData();
    checkWhoopConnection();
    loadHabitPreferences();
    loadUserId();
    calculateStreak();
    
    // Show instructions on first visit
    const hasSeenInstructions = localStorage.getItem('hasSeenCheckInInstructions');
    if (!hasSeenInstructions) {
      setShowInstructions(true);
      localStorage.setItem('hasSeenCheckInInstructions', 'true');
    }
  }, []);

  const loadUserId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) setUserId(user.id);
  };

  const calculateStreak = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('daily_checkins')
        .select('date')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (!data || data.length === 0) {
        setStreak(0);
        setTotalCheckIns(0);
        return;
      }

      setTotalCheckIns(data.length);

      // Calculate streak
      let currentStreak = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < data.length; i++) {
        const checkInDate = new Date(data[i].date);
        checkInDate.setHours(0, 0, 0, 0);
        
        const expectedDate = new Date(today);
        expectedDate.setDate(expectedDate.getDate() - i);
        expectedDate.setHours(0, 0, 0, 0);

        if (checkInDate.getTime() === expectedDate.getTime()) {
          currentStreak++;
        } else {
          break;
        }
      }

      setStreak(currentStreak);
    } catch (error) {
      console.error('Error calculating streak:', error);
    }
  };

  const loadHabitPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('habit_preferences')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data?.habit_preferences && data.habit_preferences.length > 0) {
        setHabitPreferences(data.habit_preferences);
      } else {
        setShowPreferencesSelector(true);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const loadCheckInData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date().toISOString().split('T')[0];
      
      const { data: todayData } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();

      if (todayData) {
        setCurrentData(todayData);
        const type = (todayData.check_in_type || 'morning') as CheckInType;
        setCheckInType(type);
        
        if (type === 'morning' && todayData.morning_completed) {
          setHasCompletedToday(true);
        } else if (type === 'evening' && todayData.evening_completed) {
          setHasCompletedToday(true);
        }
      }

      const { data: recent } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false})
        .limit(14);

      if (recent) {
        setRecentData(recent);
      }
    } catch (error) {
      console.error('Error loading check-in data:', error);
    }
  };

  const checkWhoopConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('whoop_connections')
        .select('is_active')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      setHasWhoop(!!data);
    } catch (error) {
      console.error('Error checking WHOOP connection:', error);
    }
  };

  const handleQuickCheckInComplete = async (data: QuickCheckInData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to save your check-in.",
          variant: "destructive"
        });
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      
      const saveData = {
        user_id: user.id,
        date: today,
        check_in_type: checkInType,
        energy_level: data.energy_level,
        body_feeling: mapScoreToFeeling('body', data.body_feeling_score),
        emotion: mapScoreToFeeling('emotion', data.emotion_score),
        headspace_level: data.mind_clarity,
        spiritual_connection: mapScoreToFeeling('spirit', data.spiritual_connection),
        stress_level: data.stress_level,
        mood_score: data.emotion_score,
        [checkInType === 'morning' ? 'morning_completed' : 'evening_completed']: true
      };

      const { error } = await supabase
        .from('daily_checkins')
        .upsert(saveData as any, { onConflict: 'user_id,date' });

      if (error) throw error;

      toast({
        title: "Check-in saved!",
        description: "Your wellness snapshot has been recorded.",
      });

      // Update challenge progress in background
      try {
        await supabase.functions.invoke('update-challenge-progress', {
          body: { date: today }
        });
      } catch (error) {
        console.error('Failed to update challenge progress:', error);
      }

      setHasCompletedToday(true);
      setCurrentData(saveData);
      setViewMode('dashboard');
      loadCheckInData();
      calculateStreak();
      
      // Trigger streak celebration and show notification prompt
      checkAndCelebrate();
      setShowNotificationPrompt(true);
    } catch (error) {
      console.error('Error saving check-in:', error);
      toast({
        title: "Error",
        description: "Failed to save check-in. Please try again.",
        variant: "destructive"
      });
    }
  };

  const mapScoreToFeeling = (type: string, score: number): string => {
    if (type === 'body') {
      if (score >= 8) return 'Relaxed & At Ease';
      if (score >= 5) return 'Energised & Vital';
      return 'Tense & Tight';
    }
    if (type === 'emotion') {
      if (score >= 8) return 'Joy & Contentment';
      if (score >= 5) return 'Peace & Calm';
      if (score >= 3) return 'Sadness & Grief';
      return 'Anxiety & Fear';
    }
    if (type === 'spirit') {
      if (score >= 7) return 'Deeply Connected';
      if (score >= 4) return 'Open & Receptive';
      return 'Disconnected';
    }
    return '';
  };

  const handleDataChange = (data: any) => {
    setCurrentData(prev => ({ ...prev, ...data }));
  };

  const handleSaveDeepDive = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to save your check-in.",
          variant: "destructive"
        });
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Prepare main check-in data
      const saveData = {
        user_id: user.id,
        date: today,
        check_in_type: checkInType,
        ...currentData,
        habit_preferences: habitPreferences,
        // Map water_intake to water_intake_ml for IL computation
        water_intake_ml: currentData.water_intake || currentData.water_intake_ml,
        alcohol_units: currentData.units_consumed,
        [checkInType === 'morning' ? 'morning_completed' : 'evening_completed']: true
      };

      // Save main check-in
      const { error } = await supabase
        .from('daily_checkins')
        .upsert(saveData as any, { onConflict: 'user_id,date' });

      if (error) throw error;

      // Save smoking data if present
      if (currentData.habit_amount !== undefined && habitPreferences.includes('smoking')) {
        const { error: smokingError } = await supabase
          .from('smoking_logs')
          .upsert({
            user_id: user.id,
            date: today,
            cigarettes_per_day: currentData.habit_frequency === 'daily' ? (currentData.habit_amount || 0) : 0,
            uses_vape: currentData.habit_unit === 'vape',
            quit_attempt: currentData.support_mode || false,
            notes: currentData.habit_notes
          } as any, { onConflict: 'user_id,date' });

        if (smokingError) console.error('Error saving smoking data:', smokingError);
      }

      // Save social connection data if present
      if (currentData.meaningful_interactions !== undefined) {
        const { error: socialError } = await supabase
          .from('social_connection_logs')
          .upsert({
            user_id: user.id,
            date: today,
            meaningful_interactions: currentData.meaningful_interactions,
            felt_connected: currentData.felt_connected,
            felt_lonely: currentData.felt_lonely,
            social_activity_type: currentData.social_activity_type
          }, { onConflict: 'user_id,date' });

        if (socialError) console.error('Error saving social connection data:', socialError);
      }

      toast({
        title: "Deep Dive saved!",
        description: "Your comprehensive wellness check-in has been recorded.",
      });

      // Update challenge progress in background
      try {
        await supabase.functions.invoke('update-challenge-progress', {
          body: { date: today }
        });
      } catch (error) {
        console.error('Failed to update challenge progress:', error);
      }

      setHasCompletedToday(true);
      setViewMode('dashboard');
      loadCheckInData();
      calculateStreak();
      
      // Trigger streak celebration and show notification prompt
      checkAndCelebrate();
      setShowNotificationPrompt(true);
    } catch (error) {
      console.error('Error saving deep dive:', error);
      toast({
        title: "Error",
        description: "Failed to save check-in. Please try again.",
        variant: "destructive"
      });
    }
  };

  const renderDeepDiveStep = () => {
    const stepName = steps[currentStep];

    if (showPreferencesSelector && currentStep === 0) {
      return (
        <HabitPreferencesSelector
          onSave={() => {
            loadHabitPreferences();
            setShowPreferencesSelector(false);
          }}
          onClose={() => setShowPreferencesSelector(false)}
        />
      );
    }

    switch (stepName) {
      case 'Connected Self':
        return <ConnectedSelf initialData={currentData} onDataChange={handleDataChange} />;
      
      case 'Physical Wellbeing':
        return <PhysicalWellbeingCheckIn initialData={currentData} onDataChange={handleDataChange} />;
      
      case 'Exercise & Movement':
        return <EnhancedExerciseTracker mode="morning" initialData={currentData} onDataChange={handleDataChange} />;
      
      case 'Sleep Quality':
        return (
          <div className="space-y-4">
            {/* Optional WHOOP Import Button */}
            {hasWhoop && (
              <div className="flex justify-end">
                <WhoopImportButton 
                  onImport={(data) => {
                    handleDataChange({
                      hours_slept: data.hours_slept,
                      sleep_quality: data.sleep_quality
                    });
                  }}
                />
              </div>
            )}
            <EnhancedSleepTracker initialData={currentData} onDataChange={handleDataChange} />
          </div>
        );
      
      case 'Sunlight & Nature':
        return <SunlightTracker 
          initialData={{
            morning_sunlight: currentData.morning_sunlight,
            sunlight_minutes: currentData.sunlight_minutes,
            sunlight_location: currentData.sunlight_location,
            sunlight_streak: currentData.sunlight_streak
          }}
          onUpdate={handleDataChange} 
        />;
      
      case 'Hydration':
        return <WaterTracker 
          initialIntake={currentData.water_intake || 0}
          target={currentData.water_target || 2000}
          glassSize={currentData.glass_size || 250}
          onUpdate={(intake, target, glassSize) => {
            handleDataChange({ 
              water_intake: intake, 
              water_target: target, 
              glass_size: glassSize 
            });
          }}
        />;
      
      case 'Food & Nutrition':
        return (
          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4">Food & Nutrition Tracking</h3>
            <p className="text-muted-foreground">Nutrition tracking component</p>
          </Card>
        );
      
      case 'Alcohol Tracking':
        return <AlcoholTracker 
          initialData={{
            units_consumed: currentData.units_consumed || 0,
            drinks: currentData.drinks || [],
            risk_level: currentData.risk_level || 'green',
            craving_strength: currentData.craving_strength,
            craving_resisted: currentData.craving_resisted,
            support_mode: currentData.support_mode,
            triggers: currentData.triggers,
            coping_strategies: currentData.coping_strategies,
            delay_used: currentData.delay_used
          }}
          onDataChange={handleDataChange} 
        />;
      
      case 'Habit Tracking':
        // Determine which habit type to show based on preferences
        const habitType = habitPreferences.find(p => 
          ['smoking', 'caffeine', 'screen_time', 'shopping', 'gaming'].includes(p)
        ) as 'smoking' | 'caffeine' | 'screen_time' | 'shopping' | 'gaming' | undefined;
        
        return <HabitTracker 
          habitType={habitType || 'smoking'}
          initialData={{
            type: habitType || 'smoking',
            support_mode: currentData.support_mode || false,
            amount: currentData.habit_amount,
            unit: currentData.habit_unit,
            frequency: currentData.habit_frequency,
            urge_strength: currentData.urge_strength,
            urge_resisted: currentData.urge_resisted,
            triggers: currentData.habit_triggers,
            coping_strategies: currentData.habit_coping_strategies,
            streak_days: currentData.habit_streak_days,
            reduction_goal: currentData.habit_reduction_goal,
            delay_used: currentData.habit_delay_used,
            notes: currentData.habit_notes
          }}
          onDataChange={(data) => {
            handleDataChange({
              habit_amount: data.amount,
              habit_unit: data.unit,
              habit_frequency: data.frequency,
              urge_strength: data.urge_strength,
              urge_resisted: data.urge_resisted,
              habit_triggers: data.triggers,
              habit_coping_strategies: data.coping_strategies,
              habit_streak_days: data.streak_days,
              habit_reduction_goal: data.reduction_goal,
              habit_delay_used: data.delay_used,
              habit_notes: data.notes
            });
          }}
        />;
      
      case 'Social Connection':
        return <SocialConnectionTracker 
          initialData={{
            meaningful_interactions: currentData.meaningful_interactions,
            felt_connected: currentData.felt_connected,
            felt_lonely: currentData.felt_lonely,
            social_activity_type: currentData.social_activity_type,
            social_notes: currentData.social_notes
          }}
          onDataChange={handleDataChange}
        />;
      
      case 'Life Expansion':
        return <LifeExpansionCalendar />;
      
      case 'Evening Self-Care':
        return <EveningSelfCareCheckIn />;
      
      default:
        return (
          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4">{stepName}</h3>
            <p className="text-muted-foreground">Content for {stepName}</p>
          </Card>
        );
    }
  };

  const handleDimensionClick = (dimensionName: string) => {
    const dimension = dimensionScores.find(d => d.dimension === dimensionName);
    if (dimension) {
      setSelectedDimension(dimension);
      setShowDimensionDetail(true);
    }
  };

  const dimensionScores = calculateDimensionScores(currentData);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Instructions Modal */}
      {showInstructions && (
        <CheckInInstructions
          onClose={() => setShowInstructions(false)}
          checkInType={checkInType}
        />
      )}

      {/* Dimension Detail Dialog */}
      <DimensionDetailDialog
        open={showDimensionDetail}
        onOpenChange={setShowDimensionDetail}
        dimension={selectedDimension}
      />

      {/* Hero Header with Progress Ring */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-accent/5 to-background border border-primary/20 p-4 sm:p-6 lg:p-8">
        <div className="absolute top-0 right-0 w-32 sm:w-64 h-32 sm:h-64 bg-primary/5 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-0 left-0 w-24 sm:w-48 h-24 sm:h-48 bg-accent/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2 sm:space-y-3 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 rounded-xl bg-primary/10 backdrop-blur-sm animate-bounce-gentle shrink-0">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-2xl lg:text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent animate-slide-in-left truncate">
                  Daily Wellness
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 animate-slide-in-left line-clamp-2" style={{ animationDelay: '0.1s' }}>
                  Track your holistic wellbeing journey
                </p>
              </div>
            </div>
            
            {hasCompletedToday && (
              <div className="flex items-center gap-2 text-primary animate-bounce-celebration text-sm sm:text-base">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span className="font-semibold">Today's check-in complete!</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReminders(!showReminders)}
              className="gap-1 sm:gap-2 hover:scale-105 transition-transform"
            >
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Reminders</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowInstructions(true)}
              className="gap-1 sm:gap-2 hover:scale-105 transition-transform"
            >
              <Info className="w-4 h-4" />
              <span className="hidden sm:inline">Help</span>
            </Button>
            
            <div className="relative bg-muted/80 backdrop-blur-sm rounded-full p-1 border border-border/50 flex shadow-inner">
              <div 
                className={`absolute top-1 bottom-1 rounded-full bg-gradient-to-r transition-all duration-300 ease-out shadow-md ${
                  checkInType === 'morning' 
                    ? 'left-1 w-[calc(50%-2px)] from-amber-400/90 to-orange-400/90' 
                    : 'left-[calc(50%+1px)] w-[calc(50%-2px)] from-indigo-500/90 to-purple-500/90'
                }`}
              />
              <button
                onClick={() => setCheckInType('morning')}
                className={`relative z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 ${
                  checkInType === 'morning' 
                    ? 'text-white font-medium' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Sun className={`w-4 h-4 transition-transform duration-300 ${checkInType === 'morning' ? 'rotate-0 scale-110' : 'rotate-180 scale-100'}`} />
                <span className="text-xs font-medium hidden xs:inline">AM</span>
              </button>
              <button
                onClick={() => setCheckInType('evening')}
                className={`relative z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 ${
                  checkInType === 'evening' 
                    ? 'text-white font-medium' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Moon className={`w-4 h-4 transition-transform duration-300 ${checkInType === 'evening' ? 'rotate-0 scale-110' : '-rotate-90 scale-100'}`} />
                <span className="text-xs font-medium hidden xs:inline">PM</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reminder Snooze Panel */}
      {showReminders && (
        <div className="animate-fade-in">
          <ReminderSnooze onClose={() => setShowReminders(false)} />
        </div>
      )}

      {/* Progress & Gamification */}
      <ProgressCelebration
        streak={streak}
        totalCheckIns={totalCheckIns}
        currentProgress={currentStep}
        maxSteps={steps.length}
      />

      <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
        <TabsList className="grid w-full grid-cols-3 h-auto p-2 gap-1.5 sm:gap-2 bg-gradient-to-br from-muted/30 to-muted/50 backdrop-blur-sm rounded-2xl border border-border/50">
          <TabsTrigger 
            value="dashboard" 
            className="gap-1 flex-col py-3 px-1.5 sm:px-3 data-[state=active]:bg-gradient-to-br data-[state=active]:from-background data-[state=active]:to-background/80 data-[state=active]:shadow-lg transition-all hover:scale-105 rounded-xl min-w-0"
          >
            <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
            <span className="text-[11px] sm:text-sm font-semibold truncate w-full text-center">Dashboard</span>
            <span className="text-[10px] sm:text-xs text-muted-foreground truncate w-full text-center">Overview</span>
          </TabsTrigger>
          <TabsTrigger 
            value="quick" 
            className="gap-1 flex-col py-3 px-1.5 sm:px-3 data-[state=active]:bg-gradient-to-br data-[state=active]:from-background data-[state=active]:to-background/80 data-[state=active]:shadow-lg transition-all hover:scale-105 rounded-xl min-w-0"
          >
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
            <span className="text-[11px] sm:text-sm font-semibold truncate w-full text-center">Quick Check</span>
            <span className="text-[10px] sm:text-xs text-muted-foreground truncate w-full text-center">60 seconds</span>
          </TabsTrigger>
          <TabsTrigger 
            value="deep" 
            className="gap-1 flex-col py-3 px-1.5 sm:px-3 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary/10 data-[state=active]:to-accent/5 data-[state=active]:ring-2 data-[state=active]:ring-primary/30 data-[state=active]:shadow-xl transition-all hover:scale-105 rounded-xl relative min-w-0"
          >
            <div className="absolute -top-2 -right-1 sm:-right-2">
              <Badge className="bg-primary text-primary-foreground text-[9px] sm:text-xs px-1.5 sm:px-2 shadow-lg animate-pulse">
                Recommended
              </Badge>
            </div>
            <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
            <span className="text-[11px] sm:text-sm font-semibold truncate w-full text-center">Deep Dive</span>
            <span className="text-[10px] sm:text-xs text-muted-foreground truncate w-full text-center">Insights</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6 mt-6">
          {/* Streak Display with contextual notification prompt */}
          <StreakDisplay variant="full" showNotificationPrompt={true} />

          {hasCompletedToday ? (
            <>
              <Card className="p-6 border-2 border-green-500/20 bg-gradient-to-r from-green-500/5 to-emerald-500/5">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" />
                  <div>
                    <h3 className="text-xl font-bold">Today's Check-In Complete!</h3>
                    <p className="text-muted-foreground">
                      {checkInType === 'morning' 
                        ? "Great start! Return this evening for your reflection." 
                        : "Well done! You've completed both check-ins today."}
                    </p>
                  </div>
                </div>
              </Card>
              
              {/* Notification Prompt after completing check-in */}
              {showNotificationPrompt && (
                <ContextualNotificationPrompt 
                  context="check-in" 
                  contextValue={streak}
                  triggerOnMount={true}
                />
              )}
            </>
          ) : (
            <Card className="p-6 bg-gradient-to-r from-primary/5 to-accent/5">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">Ready for Your {checkInType === 'morning' ? 'Morning' : 'Evening'} Check-In?</h3>
                <p className="text-muted-foreground">
                  Choose your approach based on how much time you have
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                <button 
                  onClick={() => setViewMode('quick')}
                  className="p-6 rounded-lg border-2 border-border bg-background hover:border-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary">
                      <Zap className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-lg mb-1 group-hover:text-primary transition-colors">Quick Check-In</h4>
                      <p className="text-sm text-muted-foreground">⏱️ Takes ~60 seconds</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Rapid wellness snapshot across all 7 dimensions using simple sliders
                  </p>
                  <div className="flex items-center gap-2 text-sm text-primary font-medium">
                    <span>Start Quick Check-In</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </button>

                <button 
                  onClick={() => setViewMode('deep')}
                  className="p-6 rounded-lg border-2 border-primary/50 bg-gradient-to-br from-primary/5 to-accent/5 hover:border-primary hover:shadow-lg transition-all text-left group relative overflow-hidden"
                >
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-primary/20 text-primary border-primary/30">Recommended</Badge>
                  </div>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary text-primary-foreground">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-lg mb-1 group-hover:text-primary transition-colors">Deep Dive</h4>
                      <p className="text-sm text-muted-foreground">⏱️ Takes 5-10 minutes</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Comprehensive reflection with guided prompts, journal exercises, and research-backed insights
                  </p>
                  <div className="flex items-center gap-2 text-sm text-primary font-medium">
                    <span>Start Deep Dive</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </button>
              </div>
            </Card>
          )}

          {recentData.length > 0 && (
            <>
              <Card className="p-6 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-background">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      <Activity className="w-6 h-6 text-primary" />
                      Your Wellness Snapshot
                    </h3>
                    {hasWhoop && (
                      <Badge variant="secondary" className="gap-1">
                        <Activity className="w-3 h-3" />
                        WHOOP Connected
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    This holistic radar chart visualizes how you're doing across 7 interconnected life dimensions. 
                    Each axis represents a different aspect of wellbeing - from your physical energy to your sense of purpose. 
                    The overall score in the center shows your combined wellness level. Watch how changes in one area (like better sleep) 
                    ripple out to improve others (like mental clarity and emotional balance). This helps you see patterns and understand which areas need attention.
                  </p>
                </div>
                <WellnessRadar
                  scores={dimensionScores}
                  showLabels={true}
                  interactive={true}
                  onDimensionClick={handleDimensionClick}
                />
              </Card>

              {/* Behavioural Insights Feature Card */}
              <Card className="p-6 border-2 border-accent/20 bg-gradient-to-br from-accent/5 via-background to-primary/5 relative overflow-hidden animate-fade-in">
                <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-3xl" />
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-accent" />
                        Behavioural Insights Available
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Deep Dive unlocks comprehensive behavioural tracking
                      </p>
                    </div>
                    <Badge className="bg-accent text-accent-foreground">
                      Research-Backed
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                    {habitPreferences.length > 0 ? (
                      habitPreferences.map((pref) => {
                        const config = {
                          'exercise': { icon: Activity, label: 'Exercise', color: 'text-primary' },
                          'sleep': { icon: Moon, label: 'Sleep Quality', color: 'text-accent' },
                          'sunlight': { icon: Sun, label: 'Sunlight', color: 'text-primary' },
                          'hydration': { icon: Activity, label: 'Hydration', color: 'text-accent' },
                          'nutrition': { icon: Activity, label: 'Nutrition', color: 'text-primary' },
                          'alcohol': { icon: Wine, label: 'Alcohol', color: 'text-destructive' },
                          'smoking': { icon: Cigarette, label: 'Smoking', color: 'text-destructive' },
                          'caffeine': { icon: Coffee, label: 'Caffeine', color: 'text-primary' },
                          'screen_time': { icon: Smartphone, label: 'Screen Time', color: 'text-secondary' },
                          'shopping': { icon: ShoppingCart, label: 'Shopping', color: 'text-accent' },
                          'gaming': { icon: Gamepad2, label: 'Gaming', color: 'text-secondary' },
                        }[pref] || { icon: CheckCircle, label: pref, color: 'text-primary' };
                        
                        const Icon = config.icon;
                        return (
                          <div
                            key={pref}
                            className="flex items-center gap-2 p-3 rounded-lg bg-background/50 border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all hover:scale-105 group"
                          >
                            <div className={`p-2 rounded-lg bg-primary/10 group-hover:scale-110 transition-transform ${config.color}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium">{config.label}</span>
                          </div>
                        );
                      })
                    ) : (
                      <>
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-background/50 border border-dashed border-primary/30">
                          <CheckCircle className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Mood Tracking</span>
                        </div>
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-background/50 border border-dashed border-primary/30">
                          <CheckCircle className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Energy Patterns</span>
                        </div>
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-background/50 border border-dashed border-primary/30">
                          <CheckCircle className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Life Expansion</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <h4 className="font-semibold mb-2 text-sm flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-primary" />
                      What You'll Track in Deep Dive:
                    </h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-accent" />
                        Green/Amber/Red day patterns with monthly calendar
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-accent" />
                        Craving tracking, triggers, and coping strategies
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-accent" />
                        Delay timers and urge surfing techniques
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-accent" />
                        Cost savings calculator and reduction counter
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-accent" />
                        Streak tracking with motivational insights
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => setViewMode('deep')}
                    className="w-full mt-6 gap-2 hover:scale-105 transition-all shadow-lg hover:shadow-xl bg-gradient-to-r from-primary to-primary-glow group"
                    size="lg"
                  >
                    <BookOpen className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                    Start Deep Dive Journey
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Card>
            </>
          )}

          {/* Relational Pulse Prompt */}
          <RelationalPulsePrompt />

          {/* Wellness Correlation Engine - Science-backed insights */}
          <WellnessCorrelationEngine 
            userId={userId}
            hasSanctuaryAccess={false}
            onUpgrade={() => {
              toast({
                title: "Sanctuary Subscription",
                description: "Upgrade to unlock AI-powered insights and personalised challenges"
              });
            }}
          />

          {/* Personalised Challenge System */}
          {recentData.length >= 7 && (
            <PersonalisedChallenge
              userId={userId}
              onStartChallenge={() => {
                toast({
                  title: "Challenge Started! 🎯",
                  description: "Track your progress daily to see real transformation"
                });
              }}
            />
          )}

          {/* AI Therappi Support - Context-aware guidance */}
          {recentData.length >= 7 && (() => {
            const recent7 = recentData.slice(0, 7);
            const avgProcessedFood = recent7.reduce((sum, d) => sum + (d.processed_food_level || 0), 0) / 7;
            const avgAlcohol = recent7.reduce((sum, d) => sum + (d.alcohol_units || 0), 0) / 7;
            const avgSleep = recent7.reduce((sum, d) => sum + (d.sleep_quality || 0), 0) / 7;
            const lowMoodDays = recent7.filter(d => {
              const moodMap: Record<string, number> = {
                'Joy & Contentment': 10, 'Peace & Calm': 8, 'Neutral': 5,
                'Sadness & Grief': 3, 'Anxiety & Fear': 2
              };
              return (moodMap[d.emotion || ''] || 5) < 4;
            }).length;

            const detectedPatterns = [];
            if (avgProcessedFood > 6) detectedPatterns.push('High processed food intake affecting sleep and mood');
            if (avgAlcohol > 2) detectedPatterns.push('Alcohol consumption patterns needing compassionate support');
            if (avgSleep < 6) detectedPatterns.push('Sleep difficulties impacting wellbeing');
            if (lowMoodDays >= 4) detectedPatterns.push('Persistent low mood or anxiety patterns');
            if (recent7.some(d => d.habit_severity === 'red')) detectedPatterns.push('Challenging habit patterns requiring support');

            const context = lowMoodDays >= 3 ? 'mental-health' : 
                           (avgProcessedFood > 6 || avgAlcohol > 2) ? 'habit-change' : 
                           'wellness-journey';

            return (
              <AITherappiSupport
                context={context}
                detectedPatterns={detectedPatterns}
                hasAccess={false} // TODO: Connect to subscription check
                onUpgrade={() => navigate('/subscription')}
              />
            );
          })()}

          {/* Pattern Insights Panel */}
          {userId && recentData.length >= 7 && (
            <PatternInsightsPanel userId={userId} />
          )}

          <InsightsPanel 
            recentData={recentData}
            hasWhoop={hasWhoop}
          />
        </TabsContent>

        <TabsContent value="quick" className="mt-6">
          <QuickCheckIn
            onComplete={handleQuickCheckInComplete}
            onSwitchToDeep={() => setViewMode('deep')}
          />
        </TabsContent>

        <TabsContent value="deep" className="mt-6 animate-fade-in">
          {/* Enhanced Progress Card with Gamification */}
          <Card className="p-8 mb-6 border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/10 rounded-full blur-2xl" />
            
            <div className="relative space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <BookOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">
                        Step {currentStep + 1} of {steps.length}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {steps[currentStep]}
                      </p>
                    </div>
                  </div>
                  
                  {/* Animated Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Journey Progress</span>
                      <span className="font-semibold">{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
                    </div>
                    <div className="relative w-full bg-muted/50 rounded-full h-3 overflow-hidden backdrop-blur-sm">
                      <div 
                        className="bg-gradient-to-r from-primary via-primary-glow to-primary rounded-full h-3 transition-all duration-500 ease-out relative overflow-hidden"
                        style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Step Counter Badges */}
                <div className="flex gap-2 ml-6">
                  {steps.map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        idx < currentStep
                          ? 'bg-primary scale-110 shadow-lg shadow-primary/50'
                          : idx === currentStep
                          ? 'bg-primary animate-pulse scale-125 ring-2 ring-primary/30 ring-offset-2 ring-offset-background'
                          : 'bg-muted scale-90'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              {/* Motivational Message */}
              {currentStep === 0 && (
                <div className="flex items-center gap-2 p-3 bg-accent/10 rounded-lg border border-accent/20 animate-scale-in">
                  <Sparkles className="w-4 h-4 text-accent" />
                  <p className="text-sm text-muted-foreground">
                    Take your time. Every insight you gain helps you understand yourself better.
                  </p>
                </div>
              )}
              
              {currentStep === steps.length - 1 && (
                <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg border border-primary/20 animate-scale-in">
                  <Trophy className="w-4 h-4 text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Almost there! Complete this final step to unlock your personalised insights.
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Animated Step Content */}
          <div className="animate-fade-in">
            {renderDeepDiveStep()}
          </div>
          
          {/* Enhanced Navigation with Progress Indicators */}
          <Card className="mt-6 p-6 bg-gradient-to-r from-background to-muted/20 border-primary/10">
            <div className="flex gap-3">
              {currentStep > 0 && (
                <Button 
                  onClick={() => setCurrentStep(currentStep - 1)} 
                  variant="outline"
                  className="gap-2 hover:scale-105 transition-all hover:shadow-lg group"
                  size="lg"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  Previous
                </Button>
              )}
              
              {currentStep < steps.length - 1 ? (
                <Button 
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="flex-1 gap-2 hover:scale-105 transition-all shadow-lg hover:shadow-xl group bg-gradient-to-r from-primary to-primary-glow"
                  size="lg"
                >
                  <span className="flex-1 text-left">
                    <span className="text-xs opacity-80 block">Next Step</span>
                    <span className="font-semibold">{steps[currentStep + 1]}</span>
                  </span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSaveDeepDive}
                  className="flex-1 gap-2 hover:scale-105 transition-all shadow-xl hover:shadow-2xl bg-gradient-to-r from-primary via-primary-glow to-primary group"
                  size="lg"
                >
                  <CheckCircle className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span className="font-bold">Complete Your Journey</span>
                  <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </Button>
              )}
              
              <Button 
                onClick={() => {
                  setViewMode('dashboard');
                  setCurrentStep(0);
                }} 
                variant="ghost"
                size="lg"
                className="hover:scale-105 transition-all"
              >
                Save & Exit
              </Button>
            </div>
            
            {/* Mini Progress Indicator */}
            <div className="mt-4 flex items-center justify-center gap-1">
              {steps.map((step, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`group relative transition-all duration-300 ${
                    idx === currentStep ? 'w-12' : 'w-8'
                  }`}
                >
                  <div className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx < currentStep
                      ? 'bg-primary'
                      : idx === currentStep
                      ? 'bg-primary shadow-lg shadow-primary/50'
                      : 'bg-muted'
                  }`} />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs opacity-0 group-hover:opacity-100 transition-opacity bg-background px-2 py-1 rounded shadow-lg whitespace-nowrap pointer-events-none">
                    {step}
                  </span>
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
